import React, { useState, useEffect } from 'react';
import { Video, Menu, X, LogOut } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service.ts';
import UserAvatar from './UserAvatar.tsx';

function Navbar() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(authService.getUser());

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    
    const checkLoginStatus = () => {
      setUser(authService.getUser());
    };
    
    checkLoginStatus();
    window.addEventListener('storage', checkLoginStatus);
    const interval = setInterval(checkLoginStatus, 500);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', checkLoginStatus);
      clearInterval(interval);
    };
  }, []);

  const handleLogout = () => {
    authService.logout();
    setUser(null);
    navigate('/');
  };

  const isScrolled = scrollY > 50;
  
  const navbarClass = isScrolled
    ? 'bg-slate-900/95 backdrop-blur-lg shadow-lg border-b border-white/10'
    : 'bg-transparent border-b border-transparent';

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${navbarClass}`}>
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-2">
          <Video className="w-8 h-8 text-purple-400" />
          <span className="text-2xl font-bold text-white">TBKMeet</span>
        </Link>

        <div className="hidden md:flex items-center space-x-8">
          {user && (
            <Link to="/dashboard" className="text-gray-300 hover:text-white transition">Panel</Link>
          )}
          <Link to="/features" className="text-gray-300 hover:text-white transition">Özellikler</Link>
          <Link to="/pricing" className="text-gray-300 hover:text-white transition">Fiyatlandırma</Link>
          <Link to="/about" className="text-gray-300 hover:text-white transition">Hakkımızda</Link>
          
          {user ? (
            <div className="flex items-center space-x-4">
              <Link 
                to="/profile" 
                className="flex items-center gap-2 text-gray-300 hover:text-white transition"
                title="Profil"
              >
                <UserAvatar
                  src={user.avatar}
                  name={user.name}
                  email={user.email}
                  provider={user.provider}
                  showProviderBadge
                  sizeClass="w-9 h-9"
                />
                <span className="text-sm font-medium max-w-[140px] truncate">{user.name}</span>
              </Link>
            </div>
          ) : (
            <>
              <Link to="/login" className="text-gray-300 hover:text-white transition">Giriş Yap</Link>
              <Link to="/register" className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition transform hover:scale-105">Hemen Başla</Link>
            </>
          )}
        </div>

        <button
          className="md:hidden text-white p-2 min-h-[44px] min-w-[44px] flex items-center justify-center -mr-2 rounded-lg hover:bg-white/10 active:scale-95 transition"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Menüyü kapat' : 'Menüyü aç'}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-slate-900/95 backdrop-blur-lg border-t border-white/10">
          <div className="px-6 py-4 space-y-4">
            {user && (
              <Link to="/dashboard" className="block text-gray-300 hover:text-white transition" onClick={() => setMobileMenuOpen(false)}>Panel</Link>
            )}
            <Link to="/features" className="block text-gray-300 hover:text-white transition" onClick={() => setMobileMenuOpen(false)}>Özellikler</Link>
            <Link to="/pricing" className="block text-gray-300 hover:text-white transition" onClick={() => setMobileMenuOpen(false)}>Fiyatlandırma</Link>
            <Link to="/about" className="block text-gray-300 hover:text-white transition" onClick={() => setMobileMenuOpen(false)}>Hakkımızda</Link>
            
            {user ? (
              <>
                <Link 
                  to="/profile" 
                  className="flex items-center gap-3 text-gray-300 hover:text-white transition" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <UserAvatar
                    src={user.avatar}
                    name={user.name}
                    email={user.email}
                    provider={user.provider}
                    showProviderBadge
                    sizeClass="w-9 h-9"
                  />
                  <span className="text-sm font-medium truncate">{user.name}</span>
                </Link>
                <button 
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center space-x-2 w-full px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition text-center justify-center"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Çıkış Yap</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="block text-gray-300 hover:text-white transition" onClick={() => setMobileMenuOpen(false)}>Giriş Yap</Link>
                <Link to="/register" className="block w-full px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition text-center" onClick={() => setMobileMenuOpen(false)}>Hemen Başla</Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;